import React, { useState, useEffect } from "react";
import { Slider, Button, Input, Select, message } from "antd";
import SPECIESDATA from "../../data/speciesImpact.json";
import { getScore } from "./helpers";
import { Parcel, Blockgroup } from "../../types";
import { withCommas, treeTypesByAbbr, sqft, acre } from "../../helpers";
import {
  INDICATORS,
  formatPercent,
  DEFAULT_TREE_TYPE,
} from "../../configs/constants";
import { FormOutlined, SaveOutlined, StopOutlined } from "@ant-design/icons";
import {
  StatisticRow,
  StatisticCell,
  Statistic,
  InputGroup,
  IconButton,
} from "../../components";
import { Scenario } from "../../types";
import _ from "lodash";
const { Option } = Select;

function getTreeType(thing: Parcel | Blockgroup) {
  const currentTreeType = thing.treeType || DEFAULT_TREE_TYPE;
  return treeTypesByAbbr.get(currentTreeType);
}

function countParcelTrees(parcel: Parcel) {
  return Math.floor(
    ((parcel.acresAdded || 0) * 43560) / getTreeType(parcel).AreaSF
  );
}

export const ScenarioTitle = ({
  scenario,
  actionScenarioTitleEdit,
  saveScenario,
}: {
  scenario: Scenario;
  actionScenarioTitleEdit: any;
  saveScenario: any;
}) => {
  const [editingTitle, setEditingTitle] = useState(false);
  const [scenarioName, setScenarioName] = useState(scenario.name);

  useEffect(() => {
    setScenarioName(scenario.name);
  }, [scenario]);

  const onInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setEditingTitle(false);
    }
    if (e.key === "Enter") {
      actionScenarioTitleEdit({
        id: scenario.id,
        value: scenarioName,
      });
      saveScenario();
      message.success("Scenario name updated");
      setEditingTitle(false);
    }
  };

  if (editingTitle) {
    return (
      <div className="scenario__heading-label-row">
        <div className="scenario__heading-label-cell scenario__heading-label-cell--input">
          <Input
            value={scenarioName}
            onChange={(e) => setScenarioName(e.target.value)}
            onKeyDown={(e) => onInputKeyDown(e)}
            size="small"
            autoFocus
          />
        </div>

        <div className="scenario__heading-label-cell">
          <IconButton
            title="Cancel scenario edit"
            onClick={() => {
              setEditingTitle(false);
            }}
          >
            <StopOutlined />
          </IconButton>
        </div>

        <div className="scenario__heading-label-cell">
          <IconButton
            title="Save scenario title"
            onClick={() => {
              actionScenarioTitleEdit({
                id: scenario.id,
                value: scenarioName,
              });
              saveScenario();
              message.success("Scenario name updated");
              setEditingTitle(false);
            }}
          >
            <SaveOutlined />
          </IconButton>
        </div>
      </div>
    );
  }

  return (
    <div className="scenario__heading-label-row">
      <div className="scenario__heading-label-cell">
        <h1
          className="scenario__heading-label"
          onClick={() => setEditingTitle(true)}
        >
          {scenario.name}
        </h1>
      </div>

      <div className="scenario__heading-label-cell">
        <IconButton
          title="Edit scenario title"
          onClick={() => setEditingTitle(true)}
        >
          <FormOutlined />
        </IconButton>
      </div>
    </div>
  );
};

export const BlockgroupRow = (props: any) => {
  const { blockgroup }: { blockgroup: Blockgroup } = props;
  const { acresAdded } = blockgroup;
  const treeCanopyPercent = blockgroup[INDICATORS.TREE_CANOPY_PERCENT.field];
  const treeEquityScore = Math.round(
    blockgroup[INDICATORS.TREE_EQUITY_SCORE.field]
  );
  const currentType = getTreeType(blockgroup);
  // const maxTrees = Math.round(
  //   acre(blockgroup["PossibleTC"] - blockgroup["TREE_CANOP"]) /
  //     (currentType.AreaSF / 43560)
  // );
  const maxTrees = Math.round(
    (blockgroup["tc_gap"] * blockgroup["TotalAcres"]) /
      (currentType.AreaSF / 43560)
  );

  const treeTarget = (blockgroup.acresAdded * 43560) / currentType.AreaSF;

  let parcelTrees = _.sumBy(blockgroup.parcels || [], (parcel) => {
    return countParcelTrees(parcel);
  });

  let progress = null;
  if (parcelTrees) {
    progress = (
      <div
        style={{
          fontSize: 10,
          paddingTop: 10,
          display: "grid",
          gridTemplateColumns: "50fr 50fr",
          gridColumnGap: "10px",
        }}
      >
        <div>
          Percent of target reached:{" "}
          <span style={{ fontWeight: 700 }}>
            {formatPercent(parcelTrees / treeTarget)}
          </span>
        </div>
        <div>
          <div
            style={{
              marginTop: 5,
              borderRadius: 5,
              overflow: "hidden",
              background: "#dddddd",
            }}
          >
            <div
              style={{
                height: 10,
                width: formatPercent(parcelTrees / treeTarget),
                background: "#f99b3e",
              }}
            ></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <StatisticRow>
        <StatisticCell label="Existing">
          <Statistic
            label="Tree Canopy Cover"
            value={formatPercent(treeCanopyPercent)}
          />

          <Statistic
            label="Current Tree Equity Score"
            value={treeEquityScore}
          />
        </StatisticCell>

        <StatisticCell
          label="Target"
          tooltip="The number of trees required to reach your desired Tree Equity Score."
          note={currentType ? `${currentType.Type} ${currentType.Size}` : ""}
          progress={progress}
          controls={
            props.user.id &&
            treeEquityScore !== 100 && (
              <>
                <InputGroup label="Select tree type" alignment="centered">
                  <Select
                    onChange={(value) => {
                      props.actionBlockgroupSpeciesPropertyEdit({
                        id: blockgroup.GEOID,
                        value,
                      });
                      props.onChange();
                    }}
                    value={blockgroup.treeType}
                    showSearch
                    size="small"
                    style={{ width: "300px" }}
                  >
                    {SPECIESDATA.map((species, idx) => (
                      <Option
                        key={idx}
                        value={species.Abbr}
                        style={{ color: "black", backgroundColor: "white" }}
                      >
                        {species.Type} {species.Size}
                      </Option>
                    ))}
                  </Select>
                </InputGroup>

                {
                  <InputGroup
                    label="How many trees do you plan to add?"
                    note={`${withCommas(
                      acre(blockgroup["PossibleTC"] - blockgroup["TREE_CANOP"])
                    )} acres of canopy available to plant`}
                    alignment="centered"
                  >
                    <Slider
                      min={0}
                      max={maxTrees}
                      step={1}
                      marks={{
                        0: 0,
                        [maxTrees]: withCommas(maxTrees),
                      }}
                      tipFormatter={(value) => {
                        return value;
                      }}
                      value={treeTarget}
                      onChange={(value: number) => {
                        return props.actionScenarioCanopyGoalEdit({
                          id: blockgroup.GEOID,
                          value: (value * currentType.AreaSF) / 43560,
                        });
                      }}
                      onAfterChange={() => {
                        return props.onChange();
                      }}
                    />
                  </InputGroup>
                }
              </>
            )
          }
        >
          {!!blockgroup.acresAdded && (
            <>
              <Statistic
                label="Number of trees"
                value={withCommas(acresAdded / (currentType.AreaSF / 43560))}
              />

              <Statistic
                label="Updated Tree Equity Score"
                value={Math.round(getScore(blockgroup))}
              />
            </>
          )}
          {treeEquityScore === 100 && (
            <div className="note note--small">
              This Blockgroup has a Tree Equity Score of 100. Consider another
              Block Group to invest in new tree canopy.
            </div>
          )}
        </StatisticCell>
      </StatisticRow>
    </>
  );
};

export const ParcelRow = ({
  parcel,
  user,
  actionParcelSpeciesPropertyEdit,
  actionParcelAcresAddedEdit,
  onChange,
}: {
  parcel: Parcel;
  user: any;
  actionParcelSpeciesPropertyEdit: any;
  actionParcelAcresAddedEdit: any;
  onChange: any;
}) => {
  let currentType = getTreeType(parcel);
  const maxTrees = Math.round(
    acre(parcel["PossibleTC"]) / (currentType.AreaSF / 43560)
  );

  const currentTreeCount = parcel.acresAdded / (currentType.AreaSF / 43560);

  return (
    <>
      <StatisticRow>
        <StatisticCell label="Existing">
          <Statistic
            label="Tree Canopy %"
            value={formatPercent(parcel.TrCan_Pct)}
          />

          <Statistic
            label="Tree Canopy Potential (sq. feet)"
            value={withCommas(sqft(parcel.PossibleTC))}
          />
        </StatisticCell>

        <StatisticCell
          label="Proposed"
          note={currentType ? `${currentType.Type} ${currentType.Size}` : ""}
          controls={
            user.id &&
            maxTrees > 0 && (
              <>
                <InputGroup
                  label="Select Tree type to add trees"
                  alignment="centered"
                >
                  <Select
                    onChange={(value) => {
                      actionParcelSpeciesPropertyEdit({
                        value,
                        id: parcel.PARCEL_ID,
                      });
                      onChange();
                    }}
                    value={parcel.treeType}
                    showSearch
                    size="small"
                    style={{ width: 200 }}
                  >
                    {SPECIESDATA.map((species, idx) => (
                      <Option
                        key={idx}
                        value={species.Abbr}
                        style={{ color: "black", backgroundColor: "white" }}
                      >
                        {species.Type} {species.Size}
                      </Option>
                    ))}
                  </Select>
                </InputGroup>

                <InputGroup
                  label="How many trees do you want to add?"
                  note={`${withCommas(
                    sqft(parcel.PossibleTC) - sqft(parcel.TREE_CANOP)
                  )} sq ft of canopy available to plant`}
                  alignment="centered"
                >
                  <Slider
                    min={0}
                    max={maxTrees}
                    marks={{
                      0: "0",
                      [maxTrees]: maxTrees.toString(),
                    }}
                    step={1}
                    value={currentTreeCount}
                    onChange={(value: number) => {
                      return actionParcelAcresAddedEdit({
                        id: parcel.PARCEL_ID,
                        value: (value * currentType.AreaSF) / 43560,
                      });
                    }}
                    onAfterChange={() => {
                      return onChange();
                    }}
                  />
                </InputGroup>
              </>
            )
          }
        >
          {parcel.acresAdded > 0 && (
            <>
              <Statistic
                label="Number of trees"
                value={withCommas(currentTreeCount)}
              />

              <Statistic
                label="Tree Canopy Added (sq. feet)"
                value={withCommas(parcel.acresAdded * 43560)}
              />
            </>
          )}
          {maxTrees <= 0 && (
            <div className="note note--small">
              This Parcel does not have space for any additional trees.
            </div>
          )}
        </StatisticCell>
      </StatisticRow>
    </>
  );
};

export const FeatureRow = (props: any) => {
  const { feature, onChange } = props;
  const [featureName, setFeatureName] = useState(feature.properties.name);
  const [showEditName, setShowEditName] = useState(false);
  const currentTreeType = feature["treeType"] || "BDM";
  let treeData = SPECIESDATA.filter((spec) => spec.Abbr === currentTreeType)[0];
  const currentTreesSQFT = sqft(
    Math.round(feature.properties.landuse.canopy || 0)
  );
  const maxTreesSQFT = sqft(
    Math.round(
      (feature.properties.landuse.canopy || 0) +
        (feature.properties.landuse.grass || 0) +
        feature.properties.landuse.other
    )
  );

  // const currentTrees = Math.floor(currentTreesSQFT / treeData.AreaSF);
  const maxTrees = Math.floor(maxTreesSQFT / treeData.AreaSF);
  const availableAreaSQFT = maxTreesSQFT - currentTreesSQFT;

  return (
    <>
      <StatisticRow>
        <StatisticCell label="Existing">
          <Statistic
            label="Tree Canopy Cover (sq. feet)"
            value={withCommas(Math.floor(currentTreesSQFT))}
          />

          <Statistic
            label="Tree Canopy Potential (sq. feet)"
            value={withCommas(Math.floor(maxTreesSQFT))}
          />
        </StatisticCell>

        <StatisticCell
          label="Proposed"
          note={feature.treeType}
          controls={
            props.user.id &&
            maxTrees > 0 && (
              <>
                <InputGroup label="Select tree type" alignment="centered">
                  <Select
                    onChange={(value) => {
                      props.actionFeatureSpeciesPropertyEdit({
                        value,
                        id: feature.id,
                      });
                      props.onChange();
                    }}
                    value={feature.properties.treeType}
                    showSearch
                    size="small"
                    style={{ width: 200 }}
                  >
                    {SPECIESDATA.map((species, idx) => (
                      <Option
                        key={idx}
                        value={species.Abbr}
                        style={{ color: "black", backgroundColor: "white" }}
                      >
                        {species.Type} {species.Size}
                      </Option>
                    ))}
                  </Select>
                </InputGroup>

                {
                  <InputGroup
                    label="How many trees do you want to add?"
                    note={`${withCommas(
                      Math.round(availableAreaSQFT)
                    )} sq ft of canopy available to plant`}
                    alignment="centered"
                  >
                    <Slider
                      min={0}
                      max={maxTrees}
                      marks={{
                        0: "0",
                        [maxTrees]: maxTrees.toString(),
                      }}
                      step={1}
                      value={feature.properties.treesAdded / treeData.AreaSF}
                      onChange={(value: number) => {
                        return props.actionFeatureAcresAddedEdit({
                          id: feature.id,
                          value: value * treeData.AreaSF,
                        });
                      }}
                      onAfterChange={() => {
                        return props.onChange();
                      }}
                    />
                  </InputGroup>
                }
              </>
            )
          }
        >
          {feature.properties.treesAdded > 0 && (
            <>
              <Statistic
                label="Number of trees"
                value={withCommas(
                  feature.properties.treesAdded / treeData.AreaSF
                )}
              />

              <Statistic
                label="Tree Canopy Added (sq. feet)"
                value={withCommas(Math.round(feature.properties.treesAdded))}
              />
            </>
          )}
          {maxTrees <= 0 && (
            <div className="note note--small">
              This Custom Area does not have space for any additional trees.
            </div>
          )}
        </StatisticCell>
      </StatisticRow>
      {props.user.id && (
        <div className="geometry-controls">
          <Button
            size="small"
            onClick={() => {
              setShowEditName(!showEditName);
            }}
          >
            Edit Name
          </Button>
        </div>
      )}
      {showEditName && (
        <>
          <label>Name</label>
          <Input
            style={{ width: "100%", marginBottom: "8px" }}
            value={featureName}
            onChange={(e) => {
              setFeatureName(e.target.value);
              feature.properties.name = e.target.value;
              onChange();
            }}
          />
        </>
      )}
    </>
  );
};
